import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Download, Printer, RefreshCw, Search, TriangleAlert, X } from 'lucide-react';
import { CallAPIPromise } from '../routeBilling/menu/dashboard/biiling/modal/comman';
import {
  selectPrefix,
  selectStationId,
  selectStationName,
  selectreportFromDate,
  setStationID,
  setStationName,
  setreportsFromDate,
} from '../utility/Redux/profile';
import { selectToken } from '../utility/Redux/security';
import {
  setListStationMasterModalStatus,
  setSpinnerLoading,
} from '../utility/Redux/modal';
import ModListStationMaster from '../utility/component/modals/modListStationMaster';

const money = (value) => {
  if (value === 0|| value==="0") {
    return ""
  } else {
    return   Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}


const mismatchClass = (value) =>
  Number(value || 0) === 0
    ? 'bg-white text-slate-700'
    : 'bg-white text-red-700 font-semibold';

const getRowSid = (row, selectedSid) => {
  const candidates = [
    row.SID,
    row.sid,
    row.STATION_ID,
    row.station_id,
    row.STATION_CODE,
    selectedSid,
  ];
  const value = candidates.find(
    (candidate) =>
      candidate !== undefined &&
      candidate !== null &&
      String(candidate).trim() !== '' &&
      String(candidate).trim() !== '0'
  );
  return value ?? '-';
};

const columnLabel = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const amountNumber = (value) => {
  const parsed = Number(String(value ?? 0).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const displayValue = (value, key) => {
  if (value === null || value === undefined || value === '') return '-';
  if (/date/i.test(key) && moment(value).isValid()) {
    return moment(value).format('DD/MM/YYYY');
  }
  if (typeof value === 'number') return money(value) || '0.00';
  return String(value);
};

const PAGE_SIZE = 20;
const SUMMARY_AMOUNT_KEYS = [
  'ISSUE_OP_DEBIT', 'ISSUE_OP_CREDIT', 'ISSUE_PRV_DEBIT', 'ISSUE_PRV_CREDIT',
  'ISSUE_CLOSING_BAL', 'COLLECTION_OP_DEBIT', 'COLLECTION_OP_CREDIT',
  'COLLECTION_PRV_DEBIT', 'COLLECTION_PRV_CREDIT', 'COLLECTION_CLOSING_BAL',
  'PRV_DEBIT_MISMATCH', 'PRV_CREDIT_MISMATCH', 'CLOSING_BAL_MISMATCH',
];

const isAmountColumn = (key, rows = []) => {
  if (/(^|_)(id|sid|code|no|number|date|type|name|account|station|description|remarks?)($|_)/i.test(key)) return false;
  if (/(amount|amt|debit|credit|balance|bal|opening|closing|mismatch|difference|diff|value|total|issue|collection)/i.test(key)) return true;
  const populated = rows.map((row) => row[key]).filter((value) => value !== null && value !== undefined && value !== '');
  return populated.length > 0 && populated.every((value) => typeof value === 'number' || /^-?[\d,]+(?:\.\d+)?$/.test(String(value).trim()));
};

const Pagination = ({ page, totalRows, onChange, alwaysVisible = false }) => {
  const pageCount = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  if (!totalRows || (!alwaysVisible && totalRows <= PAGE_SIZE)) return null;
  return (
    <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] print:hidden">
      <span className="text-xs text-slate-500">Page {page} of {pageCount} · {totalRows} records</span>
      <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1} className="rounded-md border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-40">Previous</button>
      <button type="button" onClick={() => onChange(page + 1)} disabled={page === pageCount} className="rounded-md border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-40">Next</button>
    </div>
  );
};

export default function StationBalanceMismatch() {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const prefix = useSelector(selectPrefix);
  const stationId = useSelector(selectStationId);
  const stationName = useSelector(selectStationName);
  const reduxFromDate = useSelector(selectreportFromDate);
  const reportDate = moment(reduxFromDate).format('YYYY-MM-DD');
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [detailRows, setDetailRows] = useState([]);
  const [detailSid, setDetailSid] = useState('');
  const [detailStationName, setDetailStationName] = useState('');
  const [detailFromDate, setDetailFromDate] = useState(reportDate);
  const [detailToDate, setDetailToDate] = useState(reportDate);
  const [stationPickerTarget, setStationPickerTarget] = useState('summary');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailMatchFilter, setDetailMatchFilter] = useState('all');
  const [summaryPage, setSummaryPage] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  const printRef = useRef();

  const loadMismatchDetail = (sid = detailSid, fromDate = detailFromDate, toDate = detailToDate) => {
    if (!sid || sid === '-') {
      setDetailError('Please select a station.');
      return;
    }
    if (!fromDate || !toDate || moment(fromDate).isAfter(moment(toDate))) {
      setDetailError('From Date must be before or equal to To Date.');
      return;
    }
    setDetailRows([]);
    setDetailError('');
    setDetailLoading(true);

    CallAPIPromise(
      '/api/balanceDateWiseMismatch/stationBalDateWiseMismatch',
      {
        sid: String(sid),
        from_date: fromDate,
        to_date: toDate,
        table_prefix: prefix,
      },
      token
    )
      .then((response) => {
        const result = response?.data?.data;
        setDetailRows(Array.isArray(result) ? result : []);
        setDetailPage(1);
      })
      .catch(() => {
        setDetailError('Unable to load date Wise mismatch details. Please try again.');
      })
      .finally(() => setDetailLoading(false));
  };

  const openMismatchDetail = (row) => {
    const sid = getRowSid(row, stationId);
    const monthStart = moment(reportDate).startOf('month').format('YYYY-MM-DD');
    const monthEnd = moment(reportDate).endOf('month').format('YYYY-MM-DD');
    setDetailSid(String(sid));
    setDetailStationName(row.STATION_NAME || (String(sid) === String(stationId) ? stationName : ''));
    setDetailFromDate(monthStart);
    setDetailToDate(monthEnd);
    setActiveTab('detail');
    loadMismatchDetail(sid, monthStart, monthEnd);
  };

  const detailColumns = useMemo(
    () => (detailRows.length ? Object.keys(detailRows[0]) : []),
    [detailRows]
  );
  const loadReport = () => {
    setError('');
    setLoading(true);
    dispatch(setSpinnerLoading('Loading balance mismatch report'));
    CallAPIPromise(
      '/api/balanceMismatch/stationBalanceMismatch',
      {
        sid: stationId ? String(stationId) : '',
        table_prefix:prefix,
        fromDate: moment(reduxFromDate).format('YYYY-MM-DD'),
      },
      token
    )
      .then((response) => {
        const result = response?.data?.data;
        setRows(
          Array.isArray(result)
            ? [...result].sort((a, b) => Number(a.SID || 0) - Number(b.SID || 0))
            : []
        );
        setSummaryPage(1);
      })
      .catch(() => {
        setRows([]);
        setError('Unable to load the report. Please try again.');
      })
      .finally(() => {
        setLoading(false);
        dispatch(setSpinnerLoading(false));
      });
  };

  useEffect(() => {
    loadReport();
    // Load again only when the selected station changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  const summary = useMemo(() => {
    const mismatched = rows.filter(
      (row) =>
        Number(row.PRV_DEBIT_MISMATCH) !== 0 ||
        Number(row.PRV_CREDIT_MISMATCH) !== 0 ||
        Number(row.CLOSING_BAL_MISMATCH) !== 0
    ).length;
    const closingDifference = rows.reduce(
      (sum, row) => sum + Number(row.CLOSING_BAL_MISMATCH || 0),
      0
    );
    return { mismatched, closingDifference };
  }, [rows]);

  const summaryTotals = useMemo(
    () => Object.fromEntries(SUMMARY_AMOUNT_KEYS.map((key) => [key, rows.reduce((sum, row) => sum + Number(row[key] || 0), 0)])),
    [rows]
  );
  const detailAmountColumns = useMemo(
    () => detailColumns.filter((key) => isAmountColumn(key, detailRows)),
    [detailColumns, detailRows]
  );
  const detailMismatchColumns = useMemo(
    () => detailColumns.filter((key) => /(mismatch|difference|diff)/i.test(key)),
    [detailColumns]
  );
  const filteredDetailRows = useMemo(() => {
    if (detailMatchFilter === 'all') return detailRows;
    return detailRows.filter((row) => {
      const comparisonColumns = detailMismatchColumns.length ? detailMismatchColumns : detailAmountColumns;
      const isMatched = comparisonColumns.every((key) => amountNumber(row[key]) === 0);
      return detailMatchFilter === 'matched' ? isMatched : !isMatched;
    });
  }, [detailRows, detailMatchFilter, detailMismatchColumns, detailAmountColumns]);
  const detailTotals = useMemo(
    () => Object.fromEntries(detailAmountColumns.map((key) => [key, filteredDetailRows.reduce((sum, row) => sum + amountNumber(row[key]), 0)])),
    [detailAmountColumns, filteredDetailRows]
  );

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((header) => `"${String(row[header] ?? '').replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `station-balance-mismatch-${stationId}-${moment(reduxFromDate).format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => window.print();

  return (
    <div className="h-full w-full overflow-auto bg-slate-50 p-3 text-sm md:p-5">
      <div className="mx-auto max-w-[1800px]">
        <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm print:hidden">
          <button
            type="button"
            onClick={() => setActiveTab('summary')}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${activeTab === 'summary' ? 'bg-orange text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Balance Summary
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('detail')}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${activeTab === 'detail' ? 'bg-orange text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Date Wise Mismatch
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-orange-600">Reconciliation</p>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Station Balance Mismatch</h1>
            <p className="mt-1 text-xs text-slate-500">
              {activeTab === 'summary'
                ? 'Compare issue and collection balances from the selected end date.'
                : 'Review a station mismatch across any custom date range.'}
            </p>
          </div>

          {activeTab === 'summary' ? (
          <div className="flex flex-wrap items-end gap-2 print:hidden">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => {
                  setStationPickerTarget('summary');
                  dispatch(setListStationMasterModalStatus(true));
                }}
                className={`flex h-10 min-w-[230px] items-center gap-2 border border-slate-300 bg-white px-3 text-left hover:border-orange-400 ${stationId ? 'rounded-l-lg border-r-0' : 'rounded-lg'}`}
              >
                <Search size={14} className="shrink-0 text-orange-500" />
                <span className="text-xs font-semibold text-slate-500">Station</span>
                <span className="truncate font-semibold text-slate-700">{stationId ? `${stationId} · ${stationName || 'Selected Station'}` : 'All Stations'}</span>
              </button>
              {stationId ? (
                <button
                  type="button"
                  title="Clear station"
                  aria-label="Clear station"
                  onClick={() => {
                    setRows([]);
                    dispatch(setStationID(''));
                    dispatch(setStationName(''));
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-r-lg border border-slate-300 bg-white text-slate-500 hover:border-orange-400 hover:text-orange-600"
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>

            <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
              <span className="whitespace-nowrap text-xs font-semibold text-slate-500">From Date</span>
              <input
                type="date"
                value={reportDate}
                onChange={(event) => dispatch(setreportsFromDate(event.target.value))}
                className="bg-transparent text-sm font-medium text-slate-700 outline-none"
              />
            </label>

            <button type="button" onClick={loadReport} disabled={loading} className="flex h-10 items-center gap-2 rounded-lg bg-orange px-5 font-semibold text-white disabled:opacity-60">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> {loading ? 'Loading' : 'Ok'}
            </button>
            <button type="button" onClick={exportCsv} disabled={!rows.length} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 font-semibold text-slate-700 disabled:opacity-40">
              <Download size={15} /> CSV
            </button>
            <button type="button" onClick={printReport} disabled={!rows.length} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 font-semibold text-slate-700 disabled:opacity-40">
              <Printer size={15} /> Print
            </button>
          </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2 print:hidden">
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => {
                    setStationPickerTarget('detail');
                    dispatch(setListStationMasterModalStatus(true));
                  }}
                  className={`flex h-10 min-w-[240px] items-center gap-2 border border-slate-300 bg-white px-3 text-left hover:border-orange-400 ${detailSid ? 'rounded-l-lg border-r-0' : 'rounded-lg'}`}
                >
                  <Search size={14} className="shrink-0 text-orange-500" />
                  <span className="text-xs font-semibold text-slate-500">Station</span>
                  <span className="truncate font-semibold text-slate-700">{detailSid ? `${detailSid} · ${detailStationName || 'Selected Station'}` : 'Select Station'}</span>
                </button>
                {detailSid ? (
                  <button
                    type="button"
                    title="Clear station"
                    aria-label="Clear station"
                    onClick={() => {
                      setDetailSid('');
                      setDetailStationName('');
                      setDetailRows([]);
                      setDetailError('');
                    }}
                    className="flex h-10 w-10 items-center justify-center rounded-r-lg border border-slate-300 bg-white text-slate-500 hover:border-orange-400 hover:text-orange-600"
                  >
                    <X size={16} />
                  </button>
                ) : null}
              </div>
              <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
                <span className="whitespace-nowrap text-xs font-semibold text-slate-500">From Date</span>
                <input type="date" value={detailFromDate} onChange={(event) => setDetailFromDate(event.target.value)} className="bg-transparent text-sm font-medium text-slate-700 outline-none" />
              </label>
              <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
                <span className="whitespace-nowrap text-xs font-semibold text-slate-500">To Date</span>
                <input type="date" value={detailToDate} min={detailFromDate} onChange={(event) => setDetailToDate(event.target.value)} className="bg-transparent text-sm font-medium text-slate-700 outline-none" />
              </label>
              <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3">
                <span className="whitespace-nowrap text-xs font-semibold text-slate-500">Match Status</span>
                <select value={detailMatchFilter} onChange={(event) => { setDetailMatchFilter(event.target.value); setDetailPage(1); }} className="bg-transparent text-sm font-medium text-slate-700 outline-none">
                  <option value="all">All</option>
                  <option value="matched">Match</option>
                  <option value="unmatched">Unmatch</option>
                </select>
              </label>
              <button type="button" onClick={() => loadMismatchDetail()} disabled={detailLoading || !detailSid || !detailFromDate || !detailToDate} className="flex h-10 items-center gap-2 rounded-lg bg-orange px-5 font-semibold text-white disabled:opacity-50">
                <Search size={15} /> {detailLoading ? 'Loading' : 'Ok'}
              </button>
              <button type="button" onClick={printReport} disabled={!detailRows.length} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 font-semibold text-slate-700 disabled:opacity-40">
                <Printer size={15} /> Print
              </button>
            </div>
          )}
        </div>

        {activeTab === 'summary' ? <>
        {error ? <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700"><TriangleAlert size={17} />{error}</div> : null}

        <div id="print-component" ref={printRef}>
          <header className="mismatch-print-header">
            <div>
              <p>RECONCILIATION REPORT</p>
              <h1>Station Balance Mismatch</h1>
              <span>Issue Value vs Collection Value</span>
            </div>
            <dl>
              <div><dt>Station</dt><dd>{stationName || 'All Stations'}</dd></div>
              <div><dt>SID</dt><dd>{stationId || 'All'}</dd></div>
              <div><dt>From Date</dt><dd>{moment(reduxFromDate).format('DD/MM/YYYY')}</dd></div>
              <div><dt>Accounts</dt><dd>{rows.length}</dd></div>
              <div><dt>Mismatches</dt><dd>{summary.mismatched}</dd></div>
            </dl>
          </header>

          <div className="mismatch-table-shell overflow-hidden border border-slate-400 bg-white">
          <div className="mismatch-table-scroll overflow-x-auto">
            <table className="w-full min-w-[1550px] border-collapse text-xs">
              <thead>
                <tr>
                  <th colSpan="16" className="border-b border-slate-500 bg-white px-3 py-2 text-center text-base font-bold text-black">Station Balance Mismatch</th>
                </tr>
                <tr className="border-b border-slate-500 bg-white text-slate-700">
                  <th className="border-r border-slate-500 bg-white px-2 py-2"></th>
                  <th colSpan="6" className="border-r border-slate-500 bg-white px-3 py-2 text-center text-slate-700">ISSUE Value</th>
                  <th colSpan="6" style={{ backgroundColor: '#fce4d6', color: '#9c5a2c' }} className="border-r border-slate-500 px-3 py-2 text-center">Collection Value</th>
                  <th colSpan="3" className="bg-white px-3 py-2 text-center text-red-700">Difference</th>
                </tr>
                <tr className="border-b border-slate-500 bg-white text-slate-700">
                  <th className="border-r border-slate-500 px-2 py-2 text-center font-bold text-slate-800">SID</th>
                  {['Issue OP Debit', 'Issue OP Credit', 'Debit', 'Credit', 'Closing', 'Issue Closing Bal Type'].map((label) => <th key={`issue-${label}`} style={label.includes('Type') ? { width: 58 } : undefined} className={`border-r border-slate-500 px-2 py-2 ${label.includes('Type') ? 'text-center' : 'text-right'}`}>{label}</th>)}
                  {['Collection OP Debit', 'Collection OP Credit', 'Debit', 'Credit', 'Collection Closing', 'Collection Closing Bal Type'].map((label) => <th key={`collection-${label}`} style={{ backgroundColor: '#fce4d6', color: '#9c5a2c', ...(label.includes('Type') ? { width: 58 } : {}) }} className={`border-r border-slate-500 px-2 py-2 ${label.includes('Type') ? 'text-center' : 'text-right'}`}>{label}</th>)}
                  {['Debit Mismatch', 'Credit Mismatch', 'Closing Mismatch'].map((label) => <th key={label} className="border-r border-slate-500 px-2 py-2 text-right text-red-700">{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {!loading && !rows.length ? (
                  <tr><td colSpan="16" className="px-4 py-16 text-center text-slate-400">No records found for the selected filters.</td></tr>
                ) : rows.map((row, rowIndex) => (
                  <tr
                    key={`${row.SID}-${row.ACCOUNT_NO}`}
                    onClick={() => openMismatchDetail(row)}
                    className={`cursor-pointer border-b border-slate-500 hover:bg-orange-50 focus-within:bg-orange-50 ${Math.floor(rowIndex / PAGE_SIZE) + 1 !== summaryPage ? 'screen-page-hidden' : ''}`}
                    title="Click to view date wise mismatch details"
                  >
                    <td className="border-r border-slate-500 px-2 py-2 text-center font-semibold text-slate-700">{row.SID || getRowSid(row, stationId)}</td>
                    {[row.ISSUE_OP_DEBIT, row.ISSUE_OP_CREDIT, row.ISSUE_PRV_DEBIT, row.ISSUE_PRV_CREDIT, row.ISSUE_CLOSING_BAL].map((value, index) => <td key={`issue-${index}`} className="border-r border-slate-500 px-2 py-2 text-right tabular-nums">{money(value)}</td>)}
                    <td style={{ width: 58 }} className="border-r border-slate-500 px-1 py-2 text-center">{row.ISSUE_CLOSING_BAL_TYPE}</td>
                    {[row.COLLECTION_OP_DEBIT, row.COLLECTION_OP_CREDIT, row.COLLECTION_PRV_DEBIT, row.COLLECTION_PRV_CREDIT, row.COLLECTION_CLOSING_BAL].map((value, index) => <td key={`collection-${index}`} style={{ backgroundColor: '#fce4d6', color: '#9c5a2c' }} className="border-r border-slate-500 px-2 py-2 text-right tabular-nums">{money(value)}</td>)}
                    <td style={{ backgroundColor: '#fce4d6', color: '#9c5a2c', width: 58 }} className="border-r border-slate-500 px-1 py-2 text-center">{row.COLLECTION_CLOSING_BAL_TYPE}</td>
                    {[row.PRV_DEBIT_MISMATCH, row.PRV_CREDIT_MISMATCH, row.CLOSING_BAL_MISMATCH].map((value, index) => <td key={`mismatch-${index}`} className={`border-r border-slate-500 px-2 py-2 text-right tabular-nums ${mismatchClass(value)}`}>{money(value)}</td>)}
                  </tr>
                ))}
              </tbody>
              {rows.length ? <tfoot className="bg-slate-100 font-bold text-slate-900">
                <tr>
                  <td className="border-r border-t border-slate-500 px-2 py-2 text-right">Total</td>
                  {['ISSUE_OP_DEBIT', 'ISSUE_OP_CREDIT', 'ISSUE_PRV_DEBIT', 'ISSUE_PRV_CREDIT', 'ISSUE_CLOSING_BAL'].map((key) => <td key={key} className="border-r border-t border-slate-500 px-2 py-2 text-right tabular-nums">{money(summaryTotals[key]) || '0.00'}</td>)}
                  <td className="border-r border-t border-slate-500"></td>
                  {['COLLECTION_OP_DEBIT', 'COLLECTION_OP_CREDIT', 'COLLECTION_PRV_DEBIT', 'COLLECTION_PRV_CREDIT', 'COLLECTION_CLOSING_BAL'].map((key) => <td key={key} className="border-r border-t border-slate-500 px-2 py-2 text-right tabular-nums">{money(summaryTotals[key]) || '0.00'}</td>)}
                  <td className="border-r border-t border-slate-500"></td>
                  {['PRV_DEBIT_MISMATCH', 'PRV_CREDIT_MISMATCH', 'CLOSING_BAL_MISMATCH'].map((key) => <td key={key} className="border-r border-t border-slate-500 px-2 py-2 text-right tabular-nums text-red-700">{money(summaryTotals[key]) || '0.00'}</td>)}
                </tr>
              </tfoot> : null}
            </table>
          </div>
          <Pagination page={summaryPage} totalRows={rows.length} onChange={setSummaryPage} />
        </div>
        </div>
        </> : (
          <div id="print-component" ref={printRef} className="detail-print-component overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
            <header className="mismatch-print-header">
              <div><p>RECONCILIATION REPORT</p><h1>Date Wise Mismatch</h1><span>Station balance mismatch details</span></div>
              <dl>
                <div><dt>Station</dt><dd>{detailStationName || detailSid || '—'}</dd></div>
                <div><dt>SID</dt><dd>{detailSid || '—'}</dd></div>
                <div><dt>From Date</dt><dd>{moment(detailFromDate).format('DD/MM/YYYY')}</dd></div>
                <div><dt>To Date</dt><dd>{moment(detailToDate).format('DD/MM/YYYY')}</dd></div>
                <div><dt>Status</dt><dd>{detailMatchFilter === 'all' ? 'All' : detailMatchFilter === 'matched' ? 'Match' : 'Unmatch'}</dd></div>
                <div><dt>Records</dt><dd>{filteredDetailRows.length}</dd></div>
              </dl>
            </header>
            <div className="detail-print-title border-b border-slate-200 px-4 py-3">
              <h2 className="font-bold text-slate-900">Station {detailSid || '—'} Date Wise Mismatches</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {detailFromDate && detailToDate
                  ? `${moment(detailFromDate).format('DD/MM/YYYY')} to ${moment(detailToDate).format('DD/MM/YYYY')}`
                  : 'Select a station and date range to view details.'}
              </p>
            </div>
            <div className="detail-print-content max-h-[calc(100vh-300px)] overflow-auto p-4">
              {detailLoading ? <div className="py-16 text-center font-medium text-slate-500">Loading mismatch details…</div> : null}
              {detailError ? <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700"><TriangleAlert size={17} />{detailError}</div> : null}
              {!detailLoading && !detailError && !filteredDetailRows.length ? <div className="py-16 text-center text-slate-400">No records found for the selected station, date range, and match status.</div> : null}
              {!detailLoading && filteredDetailRows.length ? (
                <table className="w-full min-w-max border-collapse text-xs">
                  <thead className="sticky top-0 bg-slate-100 text-slate-700">
                    <tr>{detailColumns.map((key) => <th key={key} className={`whitespace-nowrap border border-slate-300 px-3 py-2 font-semibold ${detailAmountColumns.includes(key) ? 'text-right' : 'text-left'}`}>{columnLabel(key)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filteredDetailRows.map((detailRow, rowIndex) => (
                      <tr key={rowIndex} className={`even:bg-slate-50 hover:bg-orange-50 ${Math.floor(rowIndex / PAGE_SIZE) + 1 !== detailPage ? 'screen-page-hidden' : ''}`}>
                        {detailColumns.map((key) => <td key={key} className={`whitespace-nowrap border border-slate-300 px-3 py-2 text-slate-700 ${detailAmountColumns.includes(key) ? 'text-right tabular-nums' : 'text-left'}`}>{detailAmountColumns.includes(key) ? (money(amountNumber(detailRow[key])) || '0.00') : displayValue(detailRow[key], key)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 z-10 bg-slate-100 font-bold text-slate-900 shadow-[0_-2px_6px_rgba(15,23,42,0.08)]"><tr>
                    {detailColumns.map((key, index) => <td key={key} className={`whitespace-nowrap border border-slate-300 bg-slate-100 px-3 py-2 ${detailAmountColumns.includes(key) ? 'text-right tabular-nums' : 'text-left'}`}>{index === 0 ? 'Column Total' : detailAmountColumns.includes(key) ? (money(detailTotals[key]) || '0.00') : ''}</td>)}
                  </tr></tfoot>
                </table>
              ) : null}
            </div>
            <Pagination page={detailPage} totalRows={filteredDetailRows.length} onChange={setDetailPage} alwaysVisible />
          </div>
        )}
      </div>

      <style>{`
        .mismatch-print-header { display: none; }
        .screen-page-hidden { display: none; }
        @media print {
          @page { size: landscape; margin: 8mm; }
          #print-component {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            display: block;
            left: 0;
            max-width: none;
            position: absolute;
            top: 0;
            width: 100%;
          }
          #print-component .screen-page-hidden { display: table-row !important; }
          #print-component tfoot { display: table-footer-group !important; }
          #print-component.detail-print-component {
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          #print-component .detail-print-title {
            border-bottom: 1px solid #cbd5e1 !important;
            padding: 4px 0 6px !important;
          }
          #print-component .detail-print-content {
            max-height: none !important;
            overflow: visible !important;
            padding: 8px 0 0 !important;
          }
          #print-component.detail-print-component table {
            margin: 0 !important;
          }
          .mismatch-print-header {
            align-items: flex-end;
            border-bottom: 2px solid #1f2937;
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            padding: 0 2px 8px;
          }
          .mismatch-print-header p {
            color: #c2410c;
            font-size: 7px;
            font-weight: 700;
            letter-spacing: 1.2px;
            margin: 0 0 2px;
          }
          .mismatch-print-header h1 {
            color: #111827;
            font-size: 17px;
            line-height: 1.1;
            margin: 0;
          }
          .mismatch-print-header span {
            color: #6b7280;
            display: block;
            font-size: 8px;
            margin-top: 3px;
          }
          .mismatch-print-header dl {
            display: grid;
            gap: 3px 14px;
            grid-template-columns: repeat(3, auto);
            margin: 0;
          }
          .mismatch-print-header dl div { min-width: 65px; }
          .mismatch-print-header dt {
            color: #6b7280;
            font-size: 6px;
            font-weight: 700;
            text-transform: uppercase;
          }
          .mismatch-print-header dd {
            color: #111827;
            font-size: 8px;
            font-weight: 700;
            margin: 0;
          }
          .mismatch-table-shell,
          .mismatch-table-scroll {
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          #print-component table {
            font-size: 6.5px !important;
            min-width: 0 !important;
            table-layout: fixed;
            width: 100% !important;
          }
          #print-component th,
          #print-component td {
            line-height: 1.15;
            overflow-wrap: anywhere;
            padding: 4px 3px !important;
            position: static !important;
          }
          #print-component th:first-child,
          #print-component td:first-child { width: 4%; }
          #print-component thead tr:last-child th:nth-child(7),
          #print-component thead tr:last-child th:nth-child(13),
          #print-component tbody td:nth-child(7),
          #print-component tbody td:nth-child(13) { width: 3% !important; }
        }
      `}</style>

      <ModListStationMaster
        onRowClick={(params) => {
          if (stationPickerTarget === 'detail') {
            setDetailSid(String(params.row.CODE));
            setDetailStationName(params.row.STATION_NAME || '');
            setDetailRows([]);
            setDetailError('');
          } else {
            setRows([]);
            dispatch(setStationID(params.row.CODE));
            dispatch(setStationName(params.row.STATION_NAME));
          }
          dispatch(setListStationMasterModalStatus(false));
        }}
      />
    </div>
  );
}
